import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Controls } from '@xyflow/react';
import _ from 'lodash';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import styled from 'styled-components';
import uuid from 'uuid/v1';

import '@xyflow/react/dist/style.css';

import { useGetWorkflowExecutionStats } from 'src/app/queries/marketing/get-workflow-execution-stats';
import { useSaveWorkflowCampaignMutation } from 'src/app/queries/marketing/upsert-workflow-campaign';
import { useNotificationActions } from 'src/app/state/notifications';
import { userDispensariesAtom } from 'src/app/state/user-dispensaries';

import { useWorkflowBuilderContext } from '../../wizard/builder-context';
import {
  getSaveWorkflowCampaignPayload,
  getWorkflowEdgesFromReactFlowEdges,
  getWorkflowNodesFromReactFlowNodes,
} from '../../wizard/helpers';

import { addNodeMenuState } from './atoms/add-node-menu-atom';
import { nodeGraphLoadingState } from './atoms/workflow-and-email-editor-atoms';
import { NodeType } from './models';
import { AddNodeMenu } from './shared/add-node-menu';
import { StyledReactFlowEditor } from './styles';
import { edgeTypes, nodeTypes } from './utils';

import type { WorkflowCampaignEdge, WorkflowCampaignNode } from './models';
import type { WizardStepComponentProps } from 'src/app/components/lib/wizard';
import type { ApiErrorResponse } from 'src/app/queries/marketing/upsert-campaign';

const initialNodes: WorkflowCampaignNode[] = [
  {
    id: '-1',
    type: 'trigger',
    position: { x: 0, y: 0 },
    data: {
      nodeType: NodeType.TRIGGER,
      config: {},
    },
  },
];

type ReactFlowEditorProps = Partial<Omit<WizardStepComponentProps, 'disableCompleteButton' | 'enableCompleteButton'>> &
  Pick<WizardStepComponentProps, 'disableCompleteButton' | 'enableCompleteButton'>;

export function ReactFlowEditor({ enableCompleteButton, disableCompleteButton }: ReactFlowEditorProps) {
  const notifications = useNotificationActions();
  const { locProfile } = useRecoilValue(userDispensariesAtom);

  const [builderState, dispatch] = useWorkflowBuilderContext();
  const { selectedNodeId, nodes, edges } = builderState;
  const isEmailEnabled = locProfile.UseEmailMarketing;
  const isPushEnabled = locProfile.UsePushMarketing;
  const isSMSEnabled = locProfile.UseSMSMarketing;
  const showLeafLogixLoyalty = locProfile.ShowLeafLogixLoyalty;

  const { data: workflowExecutionStats } = useGetWorkflowExecutionStats(
    builderState.marketingCampaignId,
    new Date(Date.now() - 1000 * 60 * 60 * 24 * 30) // 30 days ago
  );

  const statsMap = useMemo(() => {
    const result = new Map(workflowExecutionStats?.map((stat) => [stat.StepId, stat]));
    const triggerEdge = edges.find((edge) => edge.source === '-1');
    result.set(-1, {
      TimesEntered: result.get(Number(triggerEdge?.target))?.TimesEntered ?? 0,
      TimesErrored: 0,
      Interruptions: [],
      StepId: -1,
    });
    return result;
  }, [workflowExecutionStats, edges]);

  const { mutate: saveWorkflowMutator, isLoading: isSaveWorkflowCampaignLoading } = useSaveWorkflowCampaignMutation();
  // this checks if the graph is loading from some other action not within the editor
  const [graphLoadingState] = useRecoilState(nodeGraphLoadingState);
  const resetPopupState = useResetRecoilState(addNodeMenuState);

  const setSelectedNode = useCallback(
    (nodeId: string, isNewNode) =>
      dispatch({
        type: 'set-selected-node',
        payload: {
          id: nodeId,
          isNewNode,
        },
      }),
    [dispatch]
  );

  const setNodes = useCallback(
    (nodes: WorkflowCampaignNode[]) => dispatch({ type: 'set-nodes', payload: nodes }),
    [dispatch]
  );
  const setEdges = useCallback(
    (edges: WorkflowCampaignEdge[]) => dispatch({ type: 'set-edges', payload: edges }),
    [dispatch]
  );

  useEffect(() => {
    if (nodes.length === 0) {
      // we should always start with a trigger node
      setNodes(initialNodes);
    }
  }, [nodes, setNodes]);

  const handleAddNode = useCallback(
    (sourceNodeId: string, nodeType: NodeType, edgeType?: 'no' | 'yes') => {
      const sourceNode = nodes.find((node) => node.id === sourceNodeId);
      if (!sourceNode) {
        return;
      }

      const lastNodeId = nodes.reduce((acc, node) => Math.max(acc, Number(node.id)), 0);
      const newNodeId = (lastNodeId + 1).toString();
      const position = {
        x: 0,
        y: 0,
      };

      let newNodes: WorkflowCampaignNode[] = [];

      switch (nodeType) {
        case NodeType.ACTION:
          if (isEmailEnabled) {
            newNodes = [
              {
                id: newNodeId,
                type: 'action',
                position,
                data: {
                  nodeType: NodeType.ACTION,
                  actionType: 'sendEmail',
                  config: {
                    emailConfig: {
                      isActive: true,
                      marketingEmailId: null,
                      metaSenderEmail: null,
                      previewText: '',
                      senderEmail: null,
                      subjectLine: '',
                      marketingTemplatePresetId: null,
                      priority: null,
                      utmTerm: null,
                    },
                    pushConfig: null,
                    savedTemplateId: null,
                    smsConfig: null,
                    pointBoostConfig: null,
                  },
                },
              },
            ];
          } else if (isPushEnabled) {
            newNodes = [
              {
                id: newNodeId,
                type: 'action',
                position,
                data: {
                  nodeType: NodeType.ACTION,
                  actionType: 'sendPushNotification',
                  config: {
                    pushConfig: {
                      isActive: true,
                      marketingPushId: null,
                      redirectPath: '',
                      body: '',
                      title: '',
                      priority: null,
                    },
                    savedTemplateId: null,
                    smsConfig: null,
                    emailConfig: null,
                    pointBoostConfig: null,
                  },
                },
              },
            ];
          } else if (isSMSEnabled) {
            newNodes = [
              {
                id: newNodeId,
                type: 'action',
                position,
                data: {
                  nodeType: NodeType.ACTION,
                  actionType: 'sendSMS',
                  config: {
                    emailConfig: null,
                    pushConfig: null,
                    savedTemplateId: null,
                    pointBoostConfig: null,
                    smsConfig: {
                      isActive: true,
                      marketingSMSId: null,
                      leafbuyerImageTemplateId: null,
                      leafbuyerMessageTemplateId: null,
                      leafbuyerSMSMarketingConfigId: null,
                      isMMS: false,
                      marketingTemplatePresetId: null,
                      priority: null,
                      utmTerm: null,
                    },
                  },
                },
              },
            ];
          } else if (showLeafLogixLoyalty) {
            newNodes = [
              {
                id: newNodeId,
                type: 'action',
                position,
                data: {
                  nodeType: NodeType.ACTION,
                  actionType: 'grantPoints',
                  config: {
                    pushConfig: null,
                    savedTemplateId: null,
                    smsConfig: null,
                    emailConfig: null,
                    pointBoostConfig: {
                      points: 0,
                    },
                  },
                },
              },
            ];
          }
          break;

        case NodeType.TIME_DELAY:
          newNodes = [
            {
              id: newNodeId,
              type: 'timeDelay',
              position,
              data: {
                nodeType: NodeType.TIME_DELAY,
                config: {
                  durationInMinutes: 1440,
                  timeOfDayToSend: null,
                  timeUnitId: 5,
                },
              },
            },
          ];
          break;

        case NodeType.CONDITIONAL:
          newNodes = [
            {
              id: newNodeId,
              type: 'conditional',
              position,
              data: {
                nodeType: NodeType.CONDITIONAL,
                conditionalType: 'madeAPurchase',
                config: {
                  conditionalReferenceNodeId: null,
                  durationInMinutes: 0,
                  segmentIds: null,
                  timeUnitId: null,
                },
              },
            },
          ];
          break;

        default:
          return;
      }

      // update the nodes and edges in reactflow
      setNodes([...nodes, ...newNodes]);

      let newEdges: WorkflowCampaignEdge[];
      const edgeTypeToCheck = edgeType ?? 'next';

      const existingEdge = edges.find((edge) => edge.source === sourceNodeId && edge.edgeType === edgeTypeToCheck);
      if (existingEdge) {
        const oldTarget = existingEdge.target;
        existingEdge.target = newNodeId;
        newEdges = [
          ...edges,
          {
            id: uuid(),
            source: newNodeId,
            target: oldTarget,
            type: 'step',
            edgeType: 'next',
          },
        ];
      } else {
        newEdges = [
          ...edges,
          {
            id: uuid(),
            source: sourceNodeId,
            target: newNodeId,
            type: 'step',
            edgeType: edgeTypeToCheck,
          },
        ];
      }

      setEdges(newEdges);
      setSelectedNode(newNodes[0].id, true);

      saveWorkflowMutator(
        getSaveWorkflowCampaignPayload(
          {
            Nodes: getWorkflowNodesFromReactFlowNodes([...nodes, ...newNodes]),
            Edges: getWorkflowEdgesFromReactFlowEdges(newEdges),
          },
          builderState
        ),
        {
          onError: (e) => {
            const error = e as ApiErrorResponse;
            const { data, status } = error.response;
            // a 400 error will return a user-friendly error message
            if (status === 400) {
              notifications.error({ message: data.Message });
            } else {
              notifications.error({ message: 'Error saving workflow' });
            }
          },
        }
      );
    },
    [
      nodes,
      setNodes,
      edges,
      setSelectedNode,
      saveWorkflowMutator,
      builderState,
      isEmailEnabled,
      isPushEnabled,
      isSMSEnabled,
      showLeafLogixLoyalty,
      setEdges,
    ]
  );

  const handleOnNodeClick = useCallback(
    (event, node) => {
      if (selectedNodeId !== node.id) {
        setSelectedNode(node.id, false);
      }
    },
    [setSelectedNode, selectedNodeId]
  );

  const [validMap, setValidMap] = useState<Map<string, boolean>>(new Map());

  const nodesDerivedWithProps = useMemo(
    () =>
      nodes?.map((node) => ({
        ...node,
        data: {
          ...node.data,
          onAddNode: handleAddNode,
          reportIsValid: (valid: boolean) => setValidMap((prev) => new Map(prev).set(node.id, valid)),
          stats: statsMap.get(Number(node.id)),
        },
      })) as WorkflowCampaignNode[],
    [nodes, handleAddNode, statsMap]
  );

  useEffect(() => {
    const allValid = nodes.every((node) => validMap.get(node.id) === true);
    if (allValid && !isSaveWorkflowCampaignLoading && !graphLoadingState) {
      enableCompleteButton();
    } else {
      disableCompleteButton();
    }
  }, [validMap, enableCompleteButton, disableCompleteButton, isSaveWorkflowCampaignLoading, graphLoadingState, nodes]);

  const edgesDerivedWithProps = useMemo(
    () =>
      edges?.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: edge.edgeType,
      })),
    [edges]
  );

  return (
    <WorkflowContainer>
      <FlowContainer>
        <StyledReactFlowEditor
          edges={edgesDerivedWithProps}
          edgeTypes={edgeTypes}
          fitView
          nodes={nodesDerivedWithProps}
          nodeTypes={nodeTypes}
          onNodeClick={handleOnNodeClick}
          onPaneClick={() => resetPopupState()}
        >
          <Controls />
        </StyledReactFlowEditor>
      </FlowContainer>
      <AddNodeMenu />
    </WorkflowContainer>
  );
}

const WorkflowContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const FlowContainer = styled.div`
  flex: 1;
  background: #fff;
`;
